import { buildApiUrl } from "../../config/api";
import { withCredentials } from "../../apiClient";
import { checkAuthStatus } from "../../utils/authStatus";
import { parseErrorResponse } from "../../utils/apiError";

export interface SupportEmailInput {
  description: string;
  userEmail?: string;
  slideNumber?: number;
  appEnv?: string;
  appName?: string;
  sessionId?: string;
  lastQuery?: string;
  lastResponse?: string;
}

export interface SupportEmailResult {
  success: boolean;
  error?: string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatHtmlValue(value: string): string {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function htmlDetail(label: string, value?: string | number): string {
  if (typeof value === "undefined" || value === null || value === "") {
    return "";
  }

  return `<b>${escapeHtml(label)}:</b> ${formatHtmlValue(String(value))}<br />`;
}

function truncateDetail(value: string | undefined, maxLength = 2000): string | undefined {
  if (!value || value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}...`;
}

function normalizeAppEnv(value?: string): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/** Add-in fds-ubs deploy sets FINSTER_ENV=ubs (see finster-addin-fds-ubs.yml). */
function isUbsFactSetAppEnv(appEnv?: string): boolean {
  return normalizeAppEnv(appEnv) === "ubs";
}

// FINSTER_ENV names a deploy target rather than a tier, so each value needs an
// explicit tier. `ubs` is the FDS UBS production deploy, which is the only
// environment UBS has access to.
const PROD_APP_ENVS = new Set(["production", "prod", "factset", "st4", "ubs", "apac"]);
const STAGING_APP_ENVS = new Set(["staging", "stage", "factset-staging", "st4-staging", "st4-uat"]);
const DEV_APP_ENVS = new Set(["development", "dev", "local", "test"]);

function getEnvironmentLabel(value?: string): string {
  const appEnv = normalizeAppEnv(value);

  if (PROD_APP_ENVS.has(appEnv)) return "Prod";
  if (STAGING_APP_ENVS.has(appEnv)) return "Staging";
  if (DEV_APP_ENVS.has(appEnv)) return "Dev";

  // An unmapped or missing app env used to fall back to "Dev", which sent
  // production reports out labelled as if they came from a dev deploy. Report
  // what we actually know instead of guessing a tier.
  return appEnv || "Unknown";
}

function getApplicationEnvironment(appName = "PowerPoint Add-in", appEnv?: string): string {
  return `${appName} (${getEnvironmentLabel(appEnv)})`;
}

export async function sendSupportEmail({
  description,
  userEmail,
  slideNumber,
  appEnv,
  appName,
  sessionId,
  lastQuery,
  lastResponse,
}: SupportEmailInput): Promise<SupportEmailResult> {
  try {
    const applicationName = appName || "PowerPoint Add-in";
    const environment = getApplicationEnvironment(appName, appEnv);
    const safeUserEmail = userEmail || "Unknown";
    const contextDetail =
      typeof slideNumber === "number" ? `Slide ${slideNumber}` : undefined;
    const includeConversation = !isUbsFactSetAppEnv(appEnv);
    const queryDetail = includeConversation ? truncateDetail(lastQuery) : undefined;
    const responseDetail = includeConversation ? truncateDetail(lastResponse) : undefined;

    const response = await fetch(
      buildApiUrl("/api/support-email"),
      withCredentials({
        method: "POST",
        body: JSON.stringify({
          internal_source: safeUserEmail.toLowerCase().endsWith("@finster.ai"),
          reply_to: userEmail,
          subject: `${applicationName} Support Request by ${safeUserEmail} (${new Date().toDateString()})`,
          html_body: `<html>
          <body style="font-family:sans-serif">
          <h2>Problem Reported for ${escapeHtml(applicationName)}</h2>
          ${htmlDetail("User", safeUserEmail)}
          ${htmlDetail("Environment", environment)}
          ${htmlDetail("Context", contextDetail)}
          ${htmlDetail(`${applicationName} Session Id`, sessionId)}
          ${htmlDetail("Query", queryDetail)}
          ${htmlDetail("Response", responseDetail)}
          <b>Message:</b><br /> ${formatHtmlValue(description)}
          </body>
          </html>`,
        }),
      })
    );

    checkAuthStatus(response);

    if (!response.ok) {
      const contentType = response.headers.get("content-type") || "";
      const error = contentType.includes("text/html")
        ? "Support email endpoint is unavailable. Please restart the add-in server and try again."
        : await parseErrorResponse(response);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
